import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../components/AppLayout';
import PasukCard from '../components/PasukCard';
import RecorderPanel from '../components/RecorderPanel';
import RecordingsList from '../components/RecordingsList';
import Card from '../design/Card';
import ErrorState from '../design/ErrorState';
import LoadingState from '../design/LoadingState';
import PageHeader from '../design/PageHeader';
import StatusChip from '../design/StatusChip';
import {
  getClass,
  getRecordingsForStudent,
  getSchool,
  getStudent,
  getStudentProgress,
} from '../utils/schoolFirestore';
import {
  getRabbiAlignmentUrlForPasuk,
  getRabbiUrlForPasuk,
  pasukKey,
  statusFromScore,
} from '../utils/pasuk';
import type { Recording, School, SchoolClass, Student, StudentProgress } from '../types';

interface StudentHomePageProps {
  schoolId: string;
  classId: string;
  studentId: string;
}

interface StudentData {
  student: Student;
  schoolClass: SchoolClass;
  school: School | null;
}

const StudentHomePage: React.FC<StudentHomePageProps> = ({ schoolId, classId, studentId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<StudentData | null>(null);
  const [progress, setProgress] = useState<StudentProgress | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadActivity = useCallback(async (): Promise<void> => {
    try {
      const [p, r] = await Promise.all([
        getStudentProgress(studentId),
        getRecordingsForStudent(schoolId, studentId, 20),
      ]);
      setProgress(p);
      setRecordings(r);
    } catch (err) {
      console.error('[school] Failed to load student activity', err);
    }
  }, [schoolId, studentId]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(false);
    try {
      const [student, schoolClass, school] = await Promise.all([
        getStudent(studentId),
        getClass(classId),
        getSchool(schoolId),
      ]);
      if (!student || !schoolClass) throw new Error('Student or class not found');
      setData({ student, schoolClass, school });
      await loadActivity();
    } catch (err) {
      console.error('[school] Failed to load student page', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [classId, schoolId, studentId, loadActivity]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState fullScreen />;
  if (error || !data) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto bg-school-canvas p-6">
        <ErrorState onRetry={load} />
      </div>
    );
  }

  const { student, schoolClass, school } = data;
  const current = schoolClass.currentPasuk;
  const currentKey = current ? pasukKey(current) : null;
  const currentProgress = currentKey ? progress?.pesukim[currentKey] : undefined;
  const currentRecordings = recordings.filter((r) => r.pasukKey === currentKey);
  const fullName = `${student.firstName} ${student.lastName}`;
  const classLine = school
    ? t('school_class_at_school', { className: schoolClass.name, school: school.name })
    : t('school_class_name', { className: schoolClass.name });

  return (
    <AppLayout userName={fullName} userSubtitle={classLine}>
      <PageHeader
        title={t('school_student_greeting', { name: student.firstName })}
        subtitle={t('school_student_subtitle')}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PasukCard
            pasuk={current}
            rabbiAudioUrl={
              current ? getRabbiUrlForPasuk(current, schoolClass.nusach, schoolClass.rabbiKey) : ''
            }
            alignmentUrl={
              current
                ? getRabbiAlignmentUrlForPasuk(current, schoolClass.nusach, schoolClass.rabbiKey)
                : ''
            }
          >
            {current && <RecorderPanel onAttemptSaved={loadActivity} />}
          </PasukCard>
        </div>

        <div className="flex flex-col gap-6">
          <Card title={t('school_my_details')}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-school-muted">{t('school_name')}</dt>
              <dd className="font-semibold">{fullName}</dd>
              <dt className="text-school-muted">{t('school_class')}</dt>
              <dd className="font-semibold">{schoolClass.name}</dd>
              {school && (
                <>
                  <dt className="text-school-muted">{t('school_school')}</dt>
                  <dd className="font-semibold">{school.name}</dd>
                </>
              )}
            </dl>
            {current && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-school-line pt-4 text-sm">
                <span className="text-school-muted">
                  {t('school_my_best_score', { score: currentProgress?.bestScore ?? 0 })}
                </span>
                <StatusChip
                  status={statusFromScore(
                    currentProgress?.bestScore,
                    currentProgress?.attempts ?? 0,
                  )}
                />
              </div>
            )}
          </Card>

          {current && (
            <Card title={t('school_my_attempts')}>
              <RecordingsList recordings={currentRecordings} />
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default StudentHomePage;
